import { useState } from 'preact/hooks';
import type { CallView } from '../state/schedule';
import { resolveCall, uploadPhotos } from '../state/completions';
import { toast } from '../state/toast';

interface SheetProps {
  call: CallView;
  onClose: () => void;
}

/** Bottom sheet for closing out a job as DONE: note, follow-up flag, photos. */
export function CompleteSheet({ call, onClose }: SheetProps) {
  const [note, setNote] = useState('');
  const [needsReturn, setNeedsReturn] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const [busy, setBusy] = useState(false);

  async function save() {
    setBusy(true);
    const ok = await resolveCall({ id: call.id, status: 'done', note: note.trim(), needsReturn });
    if (ok && files.length) await uploadPhotos(call.id, files);
    setBusy(false);
    if (ok) {
      toast('Job closed out.');
      onClose();
    }
  }

  return (
    <div class="overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div class="sheet" role="dialog" aria-modal="true" aria-label="Close out job">
        <h2 class="text-lg font-extrabold text-navy-900">
          Done — {call.development || call.address}
        </h2>
        <label class="mt-3 block text-sm font-semibold text-navy-900" for="doneNote">
          Note for the office <span class="font-normal text-slate-400">(optional)</span>
        </label>
        <textarea
          id="doneNote"
          class="tap mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
          rows={3}
          maxLength={600}
          value={note}
          onInput={(e) => setNote((e.target as HTMLTextAreaElement).value)}
        />
        <label class="tap mt-3 flex items-center gap-2 py-1 font-semibold text-navy-900">
          <input
            type="checkbox"
            class="h-5 w-5 accent-brand-600"
            checked={needsReturn}
            onChange={(e) => setNeedsReturn((e.target as HTMLInputElement).checked)}
          />
          Needs a return visit
        </label>
        <label class="btn-ghost mt-3 flex w-full items-center justify-center gap-2">
          <svg
            viewBox="0 0 24 24"
            class="h-5 w-5"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            aria-hidden="true"
          >
            <path d="M3 8h3l2-3h8l2 3h3v11H3z" stroke-linejoin="round" />
            <circle cx="12" cy="13" r="3.5" />
          </svg>
          {files.length
            ? `${files.length} photo${files.length > 1 ? 's' : ''} attached`
            : 'Add photos'}
          <input
            type="file"
            accept="image/*"
            multiple
            class="sr-only"
            onChange={(e) => setFiles([...((e.target as HTMLInputElement).files ?? [])])}
          />
        </label>
        <div class="mt-4 flex gap-2">
          <button class="btn-ghost flex-1" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            class="btn-primary flex-1"
            data-testid="sheet-save"
            onClick={save}
            disabled={busy}
          >
            {busy ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Bottom sheet for a MISSED appointment: reason + reschedule flag. */
export function MissSheet({ call, onClose }: SheetProps) {
  const [reason, setReason] = useState('');
  const [reschedule, setReschedule] = useState(true);
  const [busy, setBusy] = useState(false);

  async function save() {
    setBusy(true);
    const ok = await resolveCall({
      id: call.id,
      status: 'missed',
      note: reason.trim(),
      needsReturn: reschedule,
    });
    setBusy(false);
    if (ok) {
      toast('Marked as missed.');
      onClose();
    }
  }

  return (
    <div class="overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div class="sheet" role="dialog" aria-modal="true" aria-label="Missed appointment">
        <h2 class="text-lg font-extrabold text-missed-ink">
          Missed — {call.development || call.address}
        </h2>
        <label class="mt-3 block text-sm font-semibold text-navy-900" for="missReason">
          What happened? <span class="font-normal text-slate-400">(optional)</span>
        </label>
        <textarea
          id="missReason"
          class="tap mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
          rows={3}
          maxLength={600}
          placeholder="Nobody home, gate locked…"
          value={reason}
          onInput={(e) => setReason((e.target as HTMLTextAreaElement).value)}
        />
        <label class="tap mt-3 flex items-center gap-2 py-1 font-semibold text-navy-900">
          <input
            type="checkbox"
            class="h-5 w-5 accent-brand-600"
            checked={reschedule}
            onChange={(e) => setReschedule((e.target as HTMLInputElement).checked)}
          />
          Needs to be rescheduled
        </label>
        <div class="mt-4 flex gap-2">
          <button class="btn-ghost flex-1" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            class="btn-danger-outline flex-1"
            data-testid="sheet-save"
            onClick={save}
            disabled={busy}
          >
            {busy ? 'Saving…' : 'Confirm missed'}
          </button>
        </div>
      </div>
    </div>
  );
}
